import { useEffect, useState } from "react";
import { PhotoViewer } from "@/components/PhotoViewer";
import { ReportButton } from "@/components/ReportButton";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { Avatar, Crumbs, ErrorState, Spinner, StatusBadge } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { ConnectButton } from "@/components/connect/ConnectButton";
import { api, apiError, coverOf, imageUrl, TYPE_LABELS } from "@/lib/api";

const INTENT_LABELS = { find_cohosts: "Find co-hosts", create_experiences: "Create experiences", networking: "Networking", dating: "Dating" };

export default function MemberProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [error, setError] = useState("");
  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    setM(null);
    api.get(`/members/${id}`).then(({ data }) => setM(data)).catch((e) => setError(apiError(e)));
  }, [id]);

  const message = async () => {
    try { const { data } = await api.post("/threads/direct", { user_id: id }); navigate(`/messages/${data.id}`); } catch (e) { toast.error(apiError(e)); }
  };

  if (error) return <div className="shell py-16"><ErrorState message={error} /></div>;
  if (!m) return <Spinner />;
  return (
    <div className="shell max-w-[1000px] pb-24 pt-8" data-testid="member-profile-page">
      <Crumbs items={[{ label: "Connect", to: "/connect" }, { label: m.name }]} />
      <div className="mt-8 flex flex-col gap-6 sm:flex-row sm:items-center">
        {m.photos?.length ? (
          <button type="button" onClick={() => setViewer(0)} aria-label={`Open ${m.name}'s photos`} className="cursor-zoom-in rounded-full" data-testid="member-avatar-open"><Avatar person={m} size={128} className="border-primary/40" /></button>
        ) : <Avatar person={m} size={128} className="border-primary/40" />}
        <div className="flex-1">
          <h1 className="font-display text-5xl leading-none" data-testid="member-name">{m.name}</h1>
          <p className="mt-2 text-lg text-muted-foreground">{m.home_city}</p>
        </div>
        {user.id !== m.id ? (
          <div className="flex flex-wrap items-center gap-3">
            <ConnectButton member={m} onChange={(s) => setM((x) => ({ ...x, connection_state: s.state, connection_id: s.connection_id ?? x.connection_id }))} />
            <button type="button" className="btn-outline" onClick={message} data-testid="message-member-button"><MessageSquare className="h-4 w-4" /> Message</button>
          </div>
        ) : <Link to="/profile" className="btn-outline" data-testid="edit-own-profile-link">Edit profile</Link>}
      </div>
      {(m.photos?.length > 1 || m.video) && (
        <ul className="mt-8 flex snap-x gap-3 overflow-x-auto pb-2" data-testid="member-photo-gallery">
          {m.video && <li className="w-44 shrink-0 snap-start sm:w-56"><video src={imageUrl(m.video)} controls playsInline preload="metadata" className="aspect-[3/4] w-full rounded-xl border border-primary/50 bg-black object-cover" data-testid="member-intro-video" />{m.id !== user?.id && <ReportButton kind="video" target={m.video} className="mt-1" testId="report-member-video" />}</li>}
          {m.photos.map((p, i) => <li key={p} className="w-44 shrink-0 snap-start sm:w-56"><button type="button" onClick={() => setViewer(i)} aria-label={`Open photo ${i + 1} of ${m.photos.length}`} className="group block w-full cursor-zoom-in overflow-hidden rounded-xl border border-border"><img src={imageUrl(p)} alt={`${m.name}, photo ${i + 1}`} loading="lazy" className="aspect-[3/4] w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" data-testid={`member-photo-${i}`} /></button></li>)}
        </ul>
      )}
      {viewer !== null && <PhotoViewer photos={m.photos.map(imageUrl)} paths={m.photos} index={viewer} onIndex={setViewer} onClose={() => setViewer(null)} alt={m.name} />}
      {m.bio && <p className="mt-8 max-w-2xl whitespace-pre-line text-lg text-foreground/85">{m.bio}</p>}
      <div className="mt-8 flex flex-wrap gap-2">
        {m.intents.map((i) => <span key={i} className="chip" data-active="true">{INTENT_LABELS[i]}</span>)}
        {m.interests.map((i) => <span key={i} className="chip">{TYPE_LABELS[i] || i}</span>)}
      </div>
      <h2 className="font-display mt-14 text-3xl">Experiences</h2>
      {m.experiences.length ? (
        <ul className="mt-5 grid gap-4 sm:grid-cols-2">
          {m.experiences.map((e) => (
            <li key={e.id}><Link to={`/experiences/${e.id}`} className="surface card-hover flex items-center gap-4 overflow-hidden" data-testid={`member-experience-${e.id}`}>
              <img src={coverOf(e)} alt="" className="h-24 w-32 object-cover" />
              <div className="min-w-0 flex-1 py-3 pr-3"><p className="font-display truncate text-xl">{e.title}</p><p className="mb-2 text-sm text-muted-foreground">{e.destination}</p><StatusBadge status={e.status} /></div>
            </Link></li>
          ))}
        </ul>
      ) : <p className="mt-4 text-muted-foreground">No public experiences yet.</p>}
    </div>
  );
}
